import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import voiceStreamingService from '../services/voiceStreamingService';

const designSystem = {
  colors: {
    primary: 'rgb(5, 94, 253)',
    background: 'rgb(255, 255, 255)',
    lightBackground: 'rgb(249, 249, 251)',
    text: 'rgb(34, 35, 36)',
    textSecondary: 'rgb(107, 114, 126)',
    border: 'rgb(229, 231, 235)'
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    fontWeights: { medium: 500, semibold: 600, bold: 700 }
  },
  borderRadius: '4px',
  spacing: { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' }
};

const PrimaryButton = ({ onClick, children, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: disabled ? 'rgb(156, 163, 175)' : designSystem.colors.primary,
      color: 'white',
      borderRadius: designSystem.borderRadius,
      padding: `${designSystem.spacing.md} ${designSystem.spacing.lg}`,
      fontFamily: designSystem.typography.fontFamily,
      fontSize: '13px',
      fontWeight: designSystem.typography.fontWeights.bold,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      minHeight: '44px'
    }}
  >
    {children}
  </button>
);

const Select = ({ label, value, onChange, children }) => (
  <div style={{ marginBottom: designSystem.spacing.sm }}>
    <label style={{
      display: 'block',
      marginBottom: designSystem.spacing.xs,
      fontFamily: designSystem.typography.fontFamily,
      fontWeight: designSystem.typography.fontWeights.medium,
      color: designSystem.colors.text,
      fontSize: '13px'
    }}>
      {label}
    </label>
    <select
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: `${designSystem.spacing.md} ${designSystem.spacing.lg}`,
        border: `1px solid ${designSystem.colors.border}`,
        borderRadius: designSystem.borderRadius,
        fontSize: '14px',
        fontFamily: designSystem.typography.fontFamily,
        color: designSystem.colors.text,
        backgroundColor: designSystem.colors.background,
        minHeight: '44px'
      }}
    >
      {children}
    </select>
  </div>
);

const PersonalityPreview = ({ traits, selectedVoice, businessConfig }) => {
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('greeting');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkAudioStatus = setInterval(() => {
      const serviceIsPlaying = voiceStreamingService.isPlaying;
      const serviceIsStreaming = voiceStreamingService.isReceivingStream;
      
      if (isPlaying !== serviceIsPlaying) {
        setIsPlaying(serviceIsPlaying);
      }
      
      if (isStreaming !== serviceIsStreaming) {
        setIsStreaming(serviceIsStreaming);
      }
    }, 100);

    return () => clearInterval(checkAudioStatus);
  }, [isPlaying, isStreaming]);

  const scenarios = [
    { key: 'greeting', label: 'Greeting'},
    { key: 'hours', label: 'Business Hours'},
    { key: 'manager', label: 'Speak to Manager'},
    { key: 'company', label: 'Tell me about your company'},
    { key: 'farewell', label: 'Farewell'}
  ];

  const stopVoice = () => {
    voiceStreamingService.disconnect();
    setIsPlaying(false);
    setIsStreaming(false);
  };

  const generateResponse = async () => {
    const scenario = selectedScenario;
    
    if (loading || isPlaying || isStreaming) return;
    
    setLoading(true);
    
    try {
      // Connect fresh WebSocket for each request
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        voiceStreamingService.setAuthToken(token);
        await voiceStreamingService.connect(token);
      }

      let voiceConfig = null;
      if (selectedVoice) {
        voiceConfig = {
          provider: selectedVoice.provider,
          voice_id: selectedVoice.voice_id,
          provider_config: selectedVoice.provider_config
        };
      }
      
      const configService = (await import('../services/configService')).default;
      const response = await configService.generatePersonalityResponse(scenario, voiceConfig);

      if (response.status === 'streaming') {
        setIsStreaming(true);
        setResponses(prev => ({ ...prev, [scenario]: 'Generating voice response...' }));
      } else if (response.status === 'error') {
        console.error('❌ Voice generation error:', response.message);
        setResponses(prev => ({ ...prev, [scenario]: response.response || 'Voice generation temporarily unavailable. Please try again.' }));
      } else {
        const text = response.response;
        setResponses(prev => ({ ...prev, [scenario]: text }));
        
        if (text && voiceStreamingService.websocket?.readyState === 1) {
          try {
            const voiceConfigForStream = selectedVoice ?
              selectedVoice.provider_config : 
              { voice_id: 'alloy' };
            const provider = selectedVoice ? selectedVoice.provider : 'openai';
            
            await voiceStreamingService.streamVoicePreview(
              provider,
              voiceConfigForStream,
              text
            );
          } catch (streamError) {
            console.error('❌ TTS streaming failed:', streamError);
          }
        }
      }
    } catch (error) {
      console.error('❌ Request failed:', error);
      setResponses(prev => ({ ...prev, [scenario]: 'Error generating response' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: designSystem.colors.lightBackground,
      padding: designSystem.spacing.md,
      borderRadius: designSystem.borderRadius,
      marginTop: designSystem.spacing.lg
    }}>
      <h4 style={{
        fontFamily: designSystem.typography.fontFamily,
        fontSize: isMobile ? '16px' : '18px',
        fontWeight: designSystem.typography.fontWeights.semibold,
        color: designSystem.colors.text,
        margin: `0 0 ${designSystem.spacing.sm}`
      }}>Try Your Assistant's Personality</h4>
      <p style={{
        fontFamily: designSystem.typography.fontFamily,
        color: designSystem.colors.textSecondary,
        fontSize: isMobile ? '13px' : '14px',
        marginBottom: designSystem.spacing.lg,
        lineHeight: '1.4'
      }}>
        See how your assistant would respond in common scenarios with your current personality settings.
      </p>
      
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: designSystem.spacing.sm,
        marginBottom: designSystem.spacing.lg
      }}>
        <div style={{ flex: isMobile ? 'none' : 1 }}>
          <Select
            label="Test Scenario"
            value={selectedScenario}
            onChange={(e) => setSelectedScenario(e.target.value)}
          >
            {scenarios.map(scenario => (
              <option key={scenario.key} value={scenario.key}>
                {scenario.icon} {scenario.label}
              </option>
            ))}
          </Select>
        </div>
        
        <div style={{ 
          alignSelf: isMobile ? 'stretch' : 'flex-start',
          marginTop: isMobile ? '0' : '21px',
          display: 'flex',
          gap: designSystem.spacing.sm
        }}>
          <PrimaryButton
            onClick={generateResponse}
            disabled={loading || isPlaying || isStreaming}
          >
            {loading ? 'Loading...' : isStreaming ? 'Streaming...' : 'Preview Voice'}
          </PrimaryButton>
          {(isPlaying || isStreaming) && (
            <button
              onClick={stopVoice}
              style={{
                backgroundColor: 'rgb(239, 68, 68)',
                color: 'white',
                borderRadius: designSystem.borderRadius,
                padding: `${designSystem.spacing.md} ${designSystem.spacing.lg}`,
                fontFamily: designSystem.typography.fontFamily,
                fontSize: '13px',
                fontWeight: designSystem.typography.fontWeights.bold,
                border: 'none',
                cursor: 'pointer',
                minHeight: '44px'
              }}
            >
              Stop
            </button>
          )}
        </div>
      </div>
      
      {responses[selectedScenario] && (
        <div style={{
          backgroundColor: designSystem.colors.background,
          padding: designSystem.spacing.md,
          borderRadius: designSystem.borderRadius,
          border: `1px solid ${designSystem.colors.border}`
        }}>
          <span style={{
            fontFamily: designSystem.typography.fontFamily,
            fontSize: isMobile ? '13px' : '14px',
            color: designSystem.colors.text,
            lineHeight: '1.5'
          }}>
            <strong>Assistant ({scenarios.find(s => s.key === selectedScenario)?.label}):</strong> "{responses[selectedScenario]}"
          </span>
        </div>
      )}
    </div>
  );
};

export default PersonalityPreview;